import cx from 'classnames'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useRef, useState } from 'react'
import { ReactComponent as NewTabIcon } from 'assets/icons/add16.svg'

import 'styles'

import { Button } from 'components/Button'
import Styles from './TabRoot.module.scss'
import TabStyles from './Tab.module.scss'
import { useTabRoot } from './TabRootStore'
import useResizeObserver from 'use-resize-observer'
import { useWindowResize, useThrottledFn } from 'beautiful-react-hooks'

import { Frame } from './Frame'
import { Tab } from './Tab'
import { useBrand } from 'hooks/useBrand'
import { clamp, sortBy } from 'lodash'
import { Helmet } from 'react-helmet'
import { detectPlatform } from 'utils/workspaceUtils'
import { usePwaKeyboardShortcutHandler } from './utils'
import { useHandleMessageToRoot } from './hooks/useHandleMessageToRoot'
import { useMediaQueryState } from 'utils/isPwa'
import { Controls } from './Controls'
import { getLocationUrl } from 'hooks/useHistory'

const TAB_WIDTH_MIN = Number(TabStyles.tabWidthMin)
const TAB_WIDTH_MAX = Number(TabStyles.tabWidthMax)
const NEW_TAB_BUTTON_WIDTH = Number(TabStyles.newTabButtonWidth)

export const TabRoot = observer(function ComponentName() {
  const tabRoot = useTabRoot()
  const tabsRef = useRef<HTMLDivElement>(null)
  const [leftShadowVisible, setLeftShadowVisible] = useState(true) // change to false
  const [rightShadowVisible, setRightShadowVisible] = useState(true) // change to false
  const [width, setWidth] = useState(Infinity)
  const { name: brandName } = useBrand()
  const isWcoMode = useMediaQueryState('(display-mode: window-controls-overlay)')

  const openNewTab = useCallback(() => {
    tabRoot.addTab(getLocationUrl().origin, 'Home', true)
  }, [tabRoot])

  const updateShadows = useCallback(() => {
    if (tabsRef.current == null) {
      return
    }
    setLeftShadowVisible(tabsRef.current.scrollLeft > 0)
    setRightShadowVisible(tabsRef.current.scrollLeft + tabsRef.current.clientWidth + 1 < tabsRef.current.scrollWidth)
  }, [])

  useEffect(() => {
    if (!tabsRef.current) {
      return
    }
    const el = tabsRef.current
    const wheelListener = (ev: WheelEvent) => {
      if (!ev.shiftKey && ev.deltaY !== 0 && ev.deltaX === 0) {
        ev.preventDefault()
        ev.stopPropagation()
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        tabsRef.current!.scrollBy({ left: ev.deltaY })
      }
    }
    el.addEventListener('wheel', wheelListener, { passive: false })
    return () => {
      el.removeEventListener('wheel', wheelListener)
    }
  }, [])

  const updateWidthAndShadows = useThrottledFn(
    () => {
      setWidth(tabsRef.current?.clientWidth ?? Infinity)
      updateShadows()
    },
    50,
    undefined,
    [updateShadows],
  )

  const onResize = useCallback(() => {
    updateWidthAndShadows()
    setTimeout(updateWidthAndShadows, 100)
  }, [updateWidthAndShadows])

  useEffect(() => {
    onResize()
  }, [isWcoMode, onResize])

  useResizeObserver({ ref: tabsRef, onResize })
  useWindowResize(onResize)
  useEffect(updateShadows)
  useHandleMessageToRoot()
  usePwaKeyboardShortcutHandler(tabRoot)

  const tabWidth = clamp(Math.floor((width - NEW_TAB_BUTTON_WIDTH) / tabRoot.tabs.length), TAB_WIDTH_MIN, TAB_WIDTH_MAX)

  const tabsSortedByUuid = sortBy(
    tabRoot.tabs.map((tabEntry, index) => ({ tabEntry, index })),
    ({ tabEntry }) => tabEntry.uuid,
  )

  return (
    <div className={Styles.root}>
      <Helmet>
        <title>
          {brandName} - {detectPlatform() === 'desktop' ? 'Desktop' : 'Mobile'} app (beta)
        </title>
      </Helmet>
      <div className={cx(Styles.topEmptySpace, { [Styles.wco]: isWcoMode })} />
      <div className={Styles.topBar}>
        <Controls />
        <div
          className={cx(Styles.tabs, {
            [Styles.leftShadow]: leftShadowVisible,
            [Styles.rightShadow]: rightShadowVisible,
          })}
          ref={tabsRef}
          onScroll={updateShadows}
        >
          {tabsSortedByUuid.map(({ tabEntry, index }) => (
            <Tab index={index} width={tabWidth} key={tabEntry.uuid} tabEntry={tabEntry} />
          ))}
          <div className={TabStyles.newTabWrapper} style={{ left: tabRoot.tabs.length * tabWidth }}>
            <Button
              frontIcon={NewTabIcon}
              variant="none"
              size="md"
              className={TabStyles.newTab}
              iconClassName={TabStyles.newTabIcon}
              onClick={openNewTab}
            />
          </div>
          <div
            className={Styles.draggableEmptySpace}
            style={{ left: tabRoot.tabs.length * tabWidth + NEW_TAB_BUTTON_WIDTH }}
          />
        </div>
        <div className={Styles.betaBadge}>Beta</div>
      </div>
      {tabsSortedByUuid.map(({ tabEntry }) => (
        <Frame key={tabEntry.uuid} tabEntry={tabEntry} />
      ))}
    </div>
  )
})
